import { useState, useCallback } from 'react';
import { useAuth } from '../helpers/useAuth';
import { AlertCircle, Atom, Play, Loader2, Cpu, Code2 } from 'lucide-react';

const SUPERADMIN_EMAIL = 'ryand32@gmail.com';

const PRESET_KERNELS = [
  { id: 'bell_pair', name: 'Bell Pair', description: 'Maximally entangled 2-qubit state', qubits: 2 },
  { id: 'ghz_3', name: 'GHZ-3', description: '3-qubit Greenberger–Horne–Zeilinger state', qubits: 3 },
];

const STARTER_CODE = `import cudaq

@cudaq.kernel
def kernel():
    qs = cudaq.qvector(2)
    h(qs[0])
    x.ctrl(qs[0], qs[1])
    mz(qs)
`;

type RunResult = {
  target?: string;
  result?: Record<string, number>;
  error?: string;
};

function CountsBar({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts).sort((a, b) => (b[1] as number) - (a[1] as number));
  const max = Math.max(...entries.map((e) => e[1] as number), 1);
  const total = entries.reduce((sum, e) => sum + (e[1] as number), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {entries.map(([state, count]) => {
        const pct = ((count as number) / max) * 100;
        const fraction = (((count as number) / total) * 100).toFixed(1);
        return (
          <div key={state} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span
              style={{
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                fontSize: '0.875rem',
                color: 'var(--foreground)',
                minWidth: '4ch',
                fontWeight: 600,
              }}
            >
              |{state}⟩
            </span>
            <div
              style={{
                flex: 1,
                background: 'color-mix(in srgb, var(--foreground) 6%, transparent)',
                borderRadius: '4px',
                overflow: 'hidden',
                height: '24px',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #76b900, #2e9eff)',
                  transition: 'width 0.4s ease',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.75rem',
                  color: 'var(--foreground)',
                  fontWeight: 600,
                }}
              >
                {count} ({fraction}%)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function QuantumPage() {
  const { authState } = useAuth();
  const [mode, setMode] = useState<'predefined' | 'code'>('predefined');
  const [selectedKernel, setSelectedKernel] = useState('bell_pair');
  const [code, setCode] = useState(STARTER_CODE);
  const [shots, setShots] = useState(1000);
  const [target, setTarget] = useState<'qpp-cpu' | 'nvidia'>('qpp-cpu');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);

  const runQuantum = useCallback(async () => {
    setRunning(true);
    setResult(null);
    try {
      const body =
        mode === 'predefined'
          ? { mode: 'predefined', kernel_type: selectedKernel, shots, target }
          : { mode: 'code', kernel_code: code, shots, target };
      // Use chat endpoint with hidden quantum trigger (workaround: new /_api routes return 404)
      const res = await fetch('/_api/taskingbot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: `__QUANTUM_DIRECT__:${JSON.stringify(body)}`,
          new_task: true,
        }),
      });
      const text = await res.text();
      let data: any = null;
      try {
        data = JSON.parse(text);
      } catch {
        // If chat endpoint streamed text instead of JSON, try to extract JSON from it
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try { data = JSON.parse(jsonMatch[0]); } catch {}
        }
      }
      if (!res.ok || !data) {
        setResult({ error: data?.error || `Request failed (${res.status}): ${text.slice(0, 200)}` });
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setResult({ error: err?.message || 'Network error' });
    } finally {
      setRunning(false);
    }
  }, [mode, selectedKernel, code, shots, target]);

  // Auth gate
  if (authState.type === 'loading') {
    return (
      <div style={{ padding: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Loader2 size={16} className="animate-spin" />
        <span>Loading…</span>
      </div>
    );
  }

  if (authState.type === 'authenticated' && authState.user.email !== SUPERADMIN_EMAIL) {
    return (
      <div
        style={{
          padding: '4rem 2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          color: 'var(--foreground)',
        }}
      >
        <AlertCircle size={32} className="text-red-500" />
        <span style={{ fontWeight: 600 }}>Access denied. This page is restricted.</span>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #76b900, #2e9eff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <Atom size={22} />
        </div>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--foreground)', margin: 0 }}>
            Quantum Playground
          </h1>
          <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
            Internal · NVIDIA CUDA-Q · CPU simulator
          </span>
        </div>
      </div>

      {/* Mode tabs */}
      <div
        style={{
          marginTop: '1.5rem',
          display: 'flex',
          gap: '0.25rem',
          borderBottom: '1px solid color-mix(in srgb, var(--foreground) 12%, transparent)',
        }}
      >
        <button
          onClick={() => setMode('predefined')}
          style={{
            padding: '0.625rem 1rem',
            background: 'transparent',
            border: 'none',
            borderBottom: mode === 'predefined' ? '2px solid var(--primary)' : '2px solid transparent',
            color: mode === 'predefined' ? 'var(--foreground)' : 'var(--muted-foreground)',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          <Cpu size={14} /> Preset Kernels
        </button>
        <button
          onClick={() => setMode('code')}
          style={{
            padding: '0.625rem 1rem',
            background: 'transparent',
            border: 'none',
            borderBottom: mode === 'code' ? '2px solid var(--primary)' : '2px solid transparent',
            color: mode === 'code' ? 'var(--foreground)' : 'var(--muted-foreground)',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          <Code2 size={14} /> Custom Code
        </button>
      </div>

      {/* Body */}
      <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {mode === 'predefined' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
            {PRESET_KERNELS.map((k) => {
              const active = selectedKernel === k.id;
              return (
                <button
                  key={k.id}
                  onClick={() => setSelectedKernel(k.id)}
                  style={{
                    textAlign: 'left',
                    padding: '1rem',
                    border: `1px solid ${active ? 'var(--primary)' : 'color-mix(in srgb, var(--foreground) 12%, transparent)'}`,
                    background: active
                      ? 'color-mix(in srgb, var(--primary) 8%, transparent)'
                      : 'color-mix(in srgb, var(--foreground) 3%, transparent)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    color: 'var(--foreground)',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{k.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', marginTop: '0.25rem' }}>
                    {k.description}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--muted-foreground)', marginTop: '0.5rem' }}>
                    {k.qubits} qubits
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%',
                minHeight: '280px',
                padding: '0.75rem 1rem',
                background: 'color-mix(in srgb, var(--foreground) 4%, transparent)',
                border: '1px solid color-mix(in srgb, var(--foreground) 12%, transparent)',
                borderRadius: '8px',
                color: 'var(--foreground)',
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                fontSize: '0.85rem',
                lineHeight: 1.5,
                resize: 'vertical',
              }}
            />
            <div
              style={{
                marginTop: '0.5rem',
                fontSize: '0.7rem',
                color: 'var(--muted-foreground)',
              }}
            >
              Code must define a function named <code>kernel</code> decorated with{' '}
              <code>@cudaq.kernel</code>. Imports of <code>os</code>, <code>subprocess</code>,{' '}
              <code>socket</code>, <code>open()</code>, <code>eval()</code>, and{' '}
              <code>exec()</code> are blocked.
            </div>
          </div>
        )}

        {/* Settings + Run */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            background: 'color-mix(in srgb, var(--foreground) 3%, transparent)',
            borderRadius: '10px',
            flexWrap: 'wrap',
          }}
        >
          <label style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Shots:
            <input
              type="number"
              value={shots}
              min={1}
              max={20000}
              onChange={(e) => setShots(parseInt(e.target.value || '1000', 10))}
              style={{
                width: '6rem',
                padding: '0.25rem 0.5rem',
                background: 'var(--background)',
                border: '1px solid color-mix(in srgb, var(--foreground) 14%, transparent)',
                borderRadius: '6px',
                color: 'var(--foreground)',
                fontSize: '0.85rem',
              }}
            />
          </label>
          <label style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Target:
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value as 'qpp-cpu' | 'nvidia')}
              style={{
                padding: '0.25rem 0.5rem',
                background: 'var(--background)',
                border: '1px solid color-mix(in srgb, var(--foreground) 14%, transparent)',
                borderRadius: '6px',
                color: 'var(--foreground)',
                fontSize: '0.85rem',
              }}
            >
              <option value="qpp-cpu">qpp-cpu (CPU simulator)</option>
              <option value="nvidia">nvidia (GPU — falls back to CPU)</option>
            </select>
          </label>
          <button
            onClick={runQuantum}
            disabled={running}
            style={{
              marginLeft: 'auto',
              padding: '0.5rem 1rem',
              background: running
                ? 'color-mix(in srgb, var(--foreground) 12%, transparent)'
                : 'linear-gradient(135deg, #76b900, #2e9eff)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: running ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {running ? 'Running…' : 'Run Kernel'}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div
            style={{
              padding: '1rem 1.25rem',
              borderRadius: '10px',
              border: result.error
                ? '1px solid color-mix(in srgb, #ef4444 50%, transparent)'
                : '1px solid color-mix(in srgb, var(--foreground) 12%, transparent)',
              background: result.error
                ? 'color-mix(in srgb, #ef4444 8%, transparent)'
                : 'color-mix(in srgb, var(--foreground) 3%, transparent)',
            }}
          >
            {result.error ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: '#ef4444' }}>
                <AlertCircle size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Error</div>
                  <div style={{ fontSize: '0.85rem', fontFamily: 'JetBrains Mono, monospace' }}>
                    {result.error}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    color: 'var(--foreground)',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Measurement Distribution</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                    target: <code>{result.target}</code>
                  </span>
                </div>
                {result.result && <CountsBar counts={result.result} />}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
